import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  message,
  Typography,
  Tag,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { dispatchService, Dispatch } from '../services/dispatches';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ResizableTableHeader } from '../components/ResizableTableHeader';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';

const { Title } = Typography;

type FilterType = 'all' | 'Pending' | 'Collect' | 'Return';

export const Dispatches: React.FC = () => {
  const [dispatches, setDispatches] = useState<Dispatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canView = hasPermission(user, 'dispatches.view');
  const canCreate = hasPermission(user, 'dispatches.create');

  useEffect(() => {
    if (!canView) {
      setDispatches([]);
      return;
    }
    loadDispatches();
  }, [canView]);

  const loadDispatches = async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    try {
      const response = await dispatchService.getAll();
      setDispatches(response.data || response || []);
    } catch {
      message.error('Failed to load dispatches');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await dispatchService.delete(id);
      message.success('Dispatch deleted successfully');
      loadDispatches();
    } catch {
      message.error('Failed to delete dispatch');
    }
  };

  const formatDispatchNumber = (dispatchId: number) => {
    return `#${dispatchId.toString().padStart(7, '0')}`;
  };

  const getReferenceLabel = (record: Dispatch) => {
    if (record.type === 'business') {
      if (record.business?.name) return record.business.name;
      if (record.reference_id) return `Business #${record.reference_id}`;
      return '-';
    }
    if (record.type === 'personal') {
      if (record.customer?.name) return record.customer.name;
      if (record.reference_id) return `Customer #${record.reference_id}`;
      return '-';
    }
    return record.customer?.name || record.business?.name || '-';
  };

  const baseColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      sorter: (a: Dispatch, b: Dispatch) => a.id - b.id,
      render: (id: number) => formatDispatchNumber(id),
    },
    {
      title: 'Customer',
      dataIndex: ['customer', 'name'],
      key: 'customer',
      width: 180,
      sorter: (a: Dispatch, b: Dispatch) => getReferenceLabel(a).localeCompare(getReferenceLabel(b)),
      render: (_: string, record: Dispatch) => (
        <Tooltip title={getReferenceLabel(record)} placement="topLeft">
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {getReferenceLabel(record)}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Task Type',
      dataIndex: ['taskType', 'name'],
      key: 'taskType',
      width: 150,
      sorter: (a: Dispatch, b: Dispatch) => (a.taskType?.name || '').localeCompare(b.taskType?.name || ''),
    },
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      width: 100,
      sorter: (a: Dispatch, b: Dispatch) => (a.year || 0) - (b.year || 0),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 180,
      sorter: (a: Dispatch, b: Dispatch) => (a.location || '').localeCompare(b.location || ''),
    },
    {
      title: 'Agent',
      dataIndex: ['agent', 'name'],
      key: 'agent',
      width: 160,
      sorter: (a: Dispatch, b: Dispatch) => (a.agent?.name || '').localeCompare(b.agent?.name || ''),
    },
    {
      title: 'Create Date',
      dataIndex: 'create_date',
      key: 'create_date',
      width: 120,
      sorter: (a: Dispatch, b: Dispatch) => {
        if (!a.create_date && !b.create_date) return 0;
        if (!a.create_date) return 1;
        if (!b.create_date) return -1;
        return new Date(a.create_date).getTime() - new Date(b.create_date).getTime();
      },
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: (a: Dispatch, b: Dispatch) => (a.status || 'Pending').localeCompare(b.status || 'Pending'),
      render: (status: string) => {
        const mapStatus = (status: string | undefined): 'Pending' | 'Collect' | 'Return' => {
          if (!status) return 'Pending';
          const normalized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
          if (['Pending', 'Collect', 'Return'].includes(normalized)) {
            return normalized as 'Pending' | 'Collect' | 'Return';
          }
          return 'Pending';
        };
        const mappedStatus = mapStatus(status);
        const statusConfig = {
          Pending: { tagBgColor: '#E6F7FF', tagBorderColor: '#91D5FF', tagTextColor: '#1890ff', label: 'Pending' },
          Collect: { tagBgColor: '#F6FFED', tagBorderColor: '#73D13D', tagTextColor: '#52C41A', label: 'Collect' },
          Return: { tagBgColor: '#FFF7E6', tagBorderColor: '#FFA940', tagTextColor: '#FF8C00', label: 'Return' },
        };
        const config = statusConfig[mappedStatus];
        return (
          <Tag 
            color={config.tagTextColor} 
            style={{ 
              backgroundColor: config.tagBgColor, 
              borderColor: config.tagBorderColor, 
              color: config.tagTextColor 
            }}
          >
            {config.label}
          </Tag>
        );
      },
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const { columns: resizableColumns } = useResizableColumns(visibleColumns);

  const memoizedColumns = useMemo(
    () =>
      resizableColumns.map((col) => ({
        ...col,
        width: col.width, // Preserve width
        onHeaderCell: col.onHeaderCell, // Preserve onHeaderCell for resizing
        getSearchValue:
          col.dataIndex === 'id'
            ? (record: Dispatch) => formatDispatchNumber(record.id)
            : Array.isArray(col.dataIndex) && col.dataIndex[0] === 'customer' && col.dataIndex[1] === 'name'
            ? (record: Dispatch) => getReferenceLabel(record)
            : Array.isArray(col.dataIndex) && col.dataIndex[0] === 'taskType' && col.dataIndex[1] === 'name'
            ? (record: Dispatch) => record.taskType?.name
            : col.dataIndex === 'year'
            ? (record: Dispatch) => record.year
            : col.dataIndex === 'location'
            ? (record: Dispatch) => record.location
            : Array.isArray(col.dataIndex) && col.dataIndex[0] === 'agent' && col.dataIndex[1] === 'name'
            ? (record: Dispatch) => record.agent?.name
            : col.dataIndex === 'create_date'
            ? (record: Dispatch) => record.create_date
            : col.dataIndex === 'status'
            ? (record: Dispatch) => {
                const mapStatus = (status: string | undefined): string => {
                  if (!status) return 'Pending';
                  const normalized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
                  if (['Pending', 'Collect', 'Return'].includes(normalized)) {
                    return normalized;
                  }
                  return 'Pending';
                };
                return mapStatus(record.status);
              }
            : (undefined as unknown as (record: Dispatch) => string | number | null | undefined),
      })),
    [resizableColumns]
  );

  const filteredDispatches = useMemo(() => {
    if (filter === 'all') {
      return dispatches;
    }
    const mapStatus = (status: string | undefined): 'Pending' | 'Collect' | 'Return' => {
      if (!status) return 'Pending';
      const normalized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
      if (['Pending', 'Collect', 'Return'].includes(normalized)) {
        return normalized as 'Pending' | 'Collect' | 'Return';
      }
      return 'Pending';
    };
    return dispatches.filter(dispatch => mapStatus(dispatch.status) === filter);
  }, [dispatches, filter]);

  const { columnsWithSearch, filteredData } = useTableColumnSearch<Dispatch>(filteredDispatches, memoizedColumns as any);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Dispatches
        </Title>
        <Space>
          <ColumnVisibilityToggle
            columns={columnConfig}
            visibleKeys={visibleKeys}
            onChange={handleColumnVisibilityChange}
          />
          {canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/dispatches/new')}
            >
              New Dispatch
            </Button>
          )}
        </Space>
      </div>
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button 
            type={filter === 'all' ? 'primary' : 'default'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button 
            type={filter === 'Pending' ? 'primary' : 'default'}
            onClick={() => setFilter('Pending')}
          >
            Pending
          </Button>
          <Button 
            type={filter === 'Collect' ? 'primary' : 'default'}
            onClick={() => setFilter('Collect')}
          >
            Collect
          </Button>
          <Button 
            type={filter === 'Return' ? 'primary' : 'default'}
            onClick={() => setFilter('Return')}
          >
            Return
          </Button>
        </Space>
      </div>

      <Table
        columns={columnsWithSearch}
        dataSource={filteredData}
        loading={loading}
        rowKey="id"
        pagination={false}
        scroll={{ y: 'calc(100vh - 400px)' }}
        components={{
          header: {
            cell: ResizableTableHeader,
          },
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
          hideSelectAll: true,
          columnWidth: 0,
          renderCell: () => null,
        }}
        footer={() => (
          <div style={{ textAlign: 'left', padding: '8px 0' }}>
            Total: {filteredData.length} {filteredData.length === 1 ? 'dispatch' : 'dispatches'}
          </div>
        )}
        onRow={(record) => ({
          onClick: () => setSelectedRowKeys([record.id]),
          onDoubleClick: canView ? () => navigate(`/dispatches/${record.id}/edit`) : undefined,
          style: { cursor: canView ? 'pointer' : 'default' },
        })}
      />
    </div>
  );
};
